import { Component, ViewContainerRef, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from '../../_services/dialog.service';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { NgxSpinnerService } from 'ngx-spinner';
import { PledgePerWinDetailsModalComponent } from './ppw-details-modal-component';
import { PledgePerWinGameCompletionModalComponent } from './games/ppw-game-completion-modal.component';
import { PledgePerWinService } from '../../_services/fundraising/pledgePerWin.service';
import { PledgePerWin } from '../../_models/fundraising/ppw/PledgePerWin';
import { forkJoin } from 'rxjs';
import { PledgePerWinSortOrderModalComponent } from './ppw-sort-order-modal.component';
import { PledgePerWinTeam } from '../../_models/fundraising/ppw/PledgePerWinTeam';
import { CurrencyPipe, DatePipe } from '@angular/common';

@Component({
  selector: 'app-ppw-management',
  templateUrl: './ppw-management.component.html',
  styleUrls: ['../fundraising-management.component.css']
})

export class PledgePerWinManagementComponent implements OnInit  {

  loading = false;
  searchText: string;

  public ppws: PledgePerWin[] = [];
  public displayItems: PledgePerWin[] = [];
  public teams: PledgePerWinTeam[] = [];

  public selectedTeam = 0;

  private bsModalRef: BsModalRef;

  constructor(
    private ppwService: PledgePerWinService,
    private router: Router,
    private viewRef: ViewContainerRef,
    private dialogService: DialogService,
    private spinner: NgxSpinnerService,
    private modalService: BsModalService,
    private currencyPipe: CurrencyPipe,
    private datePipe: DatePipe
  ) { }

  ngOnInit() {
    this.loadFundraisers();
  }

  loadFundraisers() {
    this.loading = true;
    this.spinner.show();

    const teamObs = this.ppwService.getAllTeams();
    const ppwObs = this.ppwService.getAll();

    forkJoin([teamObs, ppwObs]).subscribe(data => {
      const sorted = data[1].pledgePerWins?.filter(a => a.event.active) || [];
      this.orderBySortOrder(sorted);
      this.ppws = sorted.reverse();
      this.displayItems = sorted;

      this.teams = data[0];

      this.loading = false;
      this.spinner.hide();
    },
    error => {   
      this.loading = false;
      this.spinner.hide();

      if (!error) return;

      this.dialogService.alert(
        'Uh oh something went wrong...', 
        `Unable to load fundraisers: ${error.error}`,
        this.viewRef);

    });
  }

  orderBySortOrder(events: PledgePerWin[]) {
    events.sort((a, b) => b.sortOrder - a.sortOrder);
  }

  orderByInProgress(events: PledgePerWin[]){
    events.sort((a, b) => {
      if (a.event.status === 2){
          return 1;
      }else if (b.event.status === 2){
          return -1;
      }else if (a.event.status === 1){
          return 1;
      }else if (b.event.status === 1){
          return -1;
      }else {
          return b.event.status - a.event.status;
      }
    })
  }

  public create() {
    this.router.navigate([`/portal/fundraising/ppw/event/create`]);
  }

  edit(objectId: number) {
    this.router.navigate([`/portal/fundraising/ppw/event/update/`, objectId]);
  }

  clone(objectId: number){
    this.router.navigate([`portal/fundraising/ppw/event/update/`, objectId], {  queryParams: {c: 'true'}})
  }

  updateSortOrder(ppwId: number){
    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    const initialState = {
      ppwId: ppwId,
      ppws: this.ppws,
      viewRef: this.viewRef
    };

    this.bsModalRef = this.modalService
      .show(
        PledgePerWinSortOrderModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-md', initialState }));

    this.bsModalRef.content.sortOrderChanged
        .subscribe(_ => {
          this.ngOnInit();
        })
  }

  filterByTeam() {
    if (+this.selectedTeam === 0){
      this.displayItems = [...this.ppws];
      return;
    }

    this.displayItems = this.ppws.filter(ppw => ppw.team.teamId === +this.selectedTeam);
  }

  public search() {

    this.displayItems = Array.from(this.ppws);

    if (!this.searchText) {
      return;
    }
    
    let quoteRegex = /[^\s"]+|"([^"]*)"/gi;
    let searchTerms = [];
    let match;

    do {

      match = quoteRegex.exec(this.searchText.toLowerCase());

      if (match != null) {
        searchTerms.push(match[1] ? match[1] : match[0]);
      }

    } while (match != null);


    let filtered = [];

    searchTerms.forEach(s => {
      const f = this.displayItems.filter(d =>
        (d.event.name ? d.event.name.toLowerCase().indexOf(s) !== -1 : false) ||
        (d.team.name ? d.team.name.toLowerCase().indexOf(s) !== -1 : false) ||
        !s);

      f.forEach(y => {
        filtered.push(y);
        this.displayItems.splice(this.displayItems.indexOf(y), 1);
      })
    });

    this.displayItems = filtered;

  }

  startEvent (id: number) {

    const team = this.ppws?.find(e => e.event.eventId === id)?.team;
    const activeCampaign = this.ppws?.some(e => e.event.status === 2 && e.teamId === team?.teamId);

    if (activeCampaign) {

      this.dialogService.alert(
        "Uh oh!", 
        `You already have one active campaign for ${team?.name}. You should complete the previous campaign first before starting a new one.`, 
        this.viewRef);

    } else {

      this.dialogService.confirm(
        'Ready to go?',
        'Are you sure you want to start this event? Once the event starts, it can no longer be canceled.',
        this.viewRef)
          .then((result) => {
            if (result === true) {

              this.loading = true;
              this.spinner.show();

              this.ppwService.startEvent(id)
                .subscribe(
                  ()=> {

                    this.loading = false;
                    this.spinner.hide();

                    this.dialogService.alert(
                      'Event Started',
                      'Your event has been started!',
                      this.viewRef).then(() => {
                        this.loadFundraisers();
                      });
                  },
                  error => {
                    this.dialogService.alert(
                      'Uh oh something isn\'t right here...',
                      `We were unable to start your event. Please contact support at support@connectwithcue.com. ${error.error}`,
                      this.viewRef);

                      this.loading = false;
                      this.spinner.hide();
                  });     
            }
          });
      }
  }

  cancelEvent(id: number) {
    this.dialogService.confirm(
      'Uh oh',
      'Are you sure you want to cancel this event? This action is irreversible.',
      this.viewRef)
        .then((result) => {
          if (result === true) {

            this.loading = true;
            this.spinner.show();

            this.ppwService.cancelEvent(id)
              .subscribe(
                ()=> {

                  this.loading = false;
                  this.spinner.hide();

                  this.dialogService.alert(
                    'Event cancelled',
                    'Your event has been canceled',
                    this.viewRef).then(() => {
                      this.loadFundraisers();
                    });
                },
                error => {
                  this.dialogService.alert(
                    'Uh oh something isn\'t right here...',
                    `We were unable to start your event. Please contact support at support@connectwithcue.com. ${error.error}`,
                    this.viewRef);
        
                    this.loading = false;
                    this.spinner.hide();
                }
            );     
          }
      });   
  }

  completeEvent(id: number) {
    this.dialogService.confirm(
      'Ready to wrap it up?',
      'Are you sure you want to complete this event? This action is irreversible.',
      this.viewRef)
        .then((result) => {
          if (result === true) {

            this.loading = true;
            this.spinner.show();

            this.ppwService.completeEvent(id)
              .subscribe(
                ()=> {

                  this.loading = false;
                  this.spinner.hide();

                  this.dialogService.alert(
                    'Event Completed',
                    'Your event has been completed and closed!',
                    this.viewRef).then(() => {
                      this.loadFundraisers();
                    });
                },
                error => {
                  this.dialogService.alert(
                    'Uh oh something isn\'t right here...',
                    `We were unable to complete your event. Please contact support at support@connectwithcue.com. ${error.error}`,
                    this.viewRef);

                    this.loading = false;
                    this.spinner.hide();
                }
            );     
          }
      });
  }

  openDetailsModal(ppwId: number){
    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    const initialState = {
      ppwId,
      viewRef: this.viewRef
    };

    this.bsModalRef = this.modalService
      .show(
        PledgePerWinDetailsModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-md', initialState }));
  }

  openCompletionModal(ppwId: number){
    const modalConfig = {
      backdrop: true,
      ignoreBackdropClick: true
    };

    // Pass the pledge per wins since we already have them, also pass the pledgePerWinId
    // since we already know what season the game is for
    const initialState = {
      ppws: this.ppws,
      pledgePerWinId: ppwId,
      viewRef: this.viewRef
    };

    this.bsModalRef = this.modalService
      .show(
        PledgePerWinGameCompletionModalComponent,
        Object.assign({}, modalConfig, { class: 'modal-md', initialState }));
  }

  formatTime(t: string) {
    const d = new Date(); 

    const hms = t.split(':');
    d.setHours(+hms[0] || 0);
    d.setMinutes(+hms[1] || 0);

    return d;
  }
}
